import React, { useState } from 'react';
import { 
  Users, 
  MessageSquare, 
  ThumbsUp, 
  Plus, 
  Search, 
  Tag, 
  FileDown, 
  Sparkles, 
  CheckCircle2, 
  Send,
  Paperclip,
  FileText,
  Loader2
} from 'lucide-react';
import { UserProfile, ForumPost } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { saveForumPost, uploadFileToStorage } from '../lib/firebase';

interface CommunityForumProps {
  userProfile: UserProfile;
}

const INITIAL_POSTS: ForumPost[] = [
  {
    id: 'post_1',
    userId: 'user_101',
    userName: 'Sarah Jenkins (TX Candidate)',
    paperId: '5733',
    category: 'Question Help',
    title: 'Tricks for solving Core Math 5733 fraction & ratio word problems faster?',
    content: 'Hi everyone! I keep running short on time during the 5733 Core Math calculator section, especially on multi-step proportion and fraction problems. Does anyone have a systematic elimination method or estimation strategy that works well under pressure?',
    upvotes: 24,
    tags: ['Core Math', '5733', 'Pacing Strategy'],
    createdAt: '2 hours ago',
    replies: [
      {
        id: 'rep_1',
        userId: 'user_202',
        userName: 'Prof. Marcus Vance',
        content: 'Always convert complex mixed fractions to decimal format first! Typing decimals into your onscreen calculator is 3x faster than fraction inputs.',
        upvotes: 12,
        createdAt: '1 hour ago'
      }
    ]
  },
  {
    id: 'post_2',
    userId: 'user_102',
    userName: 'Elena Rostova (PA)',
    paperId: '5622',
    category: 'Study Strategy',
    title: 'Passed PLT K-6 (5622) on my first attempt with 174! Here is my exact study routine.',
    content: 'Just received my official PDE score report and scored 174 (PA target is 160)! I spent 2 weeks memorizing Piaget vs Vygotsky stages, Erikson identity stages, and Bloom taxonomy action verbs. Attached my 1-page PDF cheat sheet in the Resource Library tab!',
    upvotes: 48,
    tags: ['PLT 5622', 'Passed Exam', 'Study Routine'],
    createdAt: 'Yesterday',
    replies: []
  },
  {
    id: 'post_3',
    userId: 'user_103',
    userName: 'David Miller (CA)',
    paperId: '5001',
    category: 'Resource Share',
    title: 'Free Flashcard Deck PDF: Elementary Science 5005 Plate Tectonics & Earth Systems',
    content: 'Compiled a clean high-yield summary deck covering convergent boundaries, rock cycles, and photosynthesis light reactions for 5005. Free to download for all PRAXIS Prep Pro members!',
    upvotes: 31,
    tags: ['Elementary 5001', '5005 Science', 'Free Notes'],
    createdAt: '2 days ago',
    replies: []
  }
];

export const CommunityForum: React.FC<CommunityForumProps> = ({ userProfile }) => {
  const [posts, setPosts] = useState<ForumPost[]>(INITIAL_POSTS);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedPaperFilter, setSelectedPaperFilter] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Active Post Reply View
  const [activePostId, setActivePostId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<string>('');

  // New Post Modal
  const [showNewPostModal, setShowNewPostModal] = useState<boolean>(false);
  const [newPostTitle, setNewPostTitle] = useState<string>('');
  const [newPostCategory, setNewPostCategory] = useState<ForumPost['category']>('Question Help');
  const [newPostPaperId, setNewPostPaperId] = useState<string>('5733');
  const [newPostContent, setNewPostContent] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);

  const handleUpvote = (postId: string) => {
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const isUpvoted = p.userUpvoted;
        return {
          ...p,
          upvotes: isUpvoted ? p.upvotes - 1 : p.upvotes + 1,
          userUpvoted: !isUpvoted
        };
      }
      return p;
    }));
  };

  const handleAddReply = (postId: string) => {
    if (!replyText.trim()) return;

    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const newRep = {
          id: 'rep_' + Date.now(),
          userId: userProfile.uid,
          userName: userProfile.displayName,
          content: replyText,
          upvotes: 0,
          createdAt: 'Just now'
        };
        return {
          ...p,
          replies: [...(p.replies || []), newRep]
        };
      }
      return p;
    }));

    setReplyText('');
  };

  const handleCreatePost = async () => {
    if (!newPostTitle.trim() || !newPostContent.trim()) return;

    let attachmentUrl: string | undefined = undefined;
    let attachmentName: string | undefined = undefined;

    if (selectedFile) {
      setUploadingFile(true);
      try {
        const uploadRes = await uploadFileToStorage(selectedFile, 'forum_attachments');
        attachmentUrl = uploadRes.url;
        attachmentName = uploadRes.name;
      } catch (err) {
        console.error('Attachment upload failed:', err);
      } finally {
        setUploadingFile(false);
      }
    }

    const newPost: Omit<ForumPost, 'id'> = {
      userId: userProfile.uid,
      userName: userProfile.displayName,
      paperId: newPostPaperId,
      category: newPostCategory,
      title: newPostTitle,
      content: newPostContent,
      attachmentUrl,
      attachmentName,
      upvotes: 1,
      userUpvoted: true,
      replies: [],
      tags: [newPostCategory, `PRAXIS ${newPostPaperId}`],
      createdAt: 'Just now'
    };

    const docId = await saveForumPost(newPost);
    setPosts(prev => [{ id: docId, ...newPost }, ...prev]);

    setShowNewPostModal(false);
    setNewPostTitle('');
    setNewPostContent('');
    setSelectedFile(null);
  };

  const filteredPosts = posts.filter(post => {
    const matchesCat = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesPaper = selectedPaperFilter === 'All' || post.paperId === selectedPaperFilter;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesPaper && matchesSearch;
  });

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider">
            <Users className="w-4 h-4" /> Peer Support & Study Resource Exchange
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
            PRAXIS Peer Community Forum
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Connect with pre-service educators nationwide, exchange exam strategies, and access shared study guides.
          </p>
        </div>

        <button
          onClick={() => setShowNewPostModal(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs sm:text-sm px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-2 shrink-0"
        >
          <Plus className="w-4 h-4" /> Start New Discussion
        </button>
      </div>

      {/* Filter Row */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
          {['All', 'Question Help', 'Study Strategy', 'Resource Share', 'State Passing Scores'].map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition ${
                selectedCategory === cat
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Search & Paper Select */}
        <div className="flex items-center gap-2">
          <select
            value={selectedPaperFilter}
            onChange={(e) => setSelectedPaperFilter(e.target.value)}
            className="bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800"
          >
            <option value="All">All Papers</option>
            {PRAXIS_PAPERS.map(p => (
              <option key={p.id} value={p.id}>
                PRAXIS {p.code}
              </option>
            ))}
          </select>

          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search forum..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white border border-slate-300 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Main Forum Posts Feed */}
      <div className="space-y-4">
        {filteredPosts.map(post => {
          const isExpanded = activePostId === post.id;

          return (
            <div
              key={post.id}
              className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4 transition hover:border-slate-300"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-bold text-slate-900">{post.userName}</span>
                  <span className="text-slate-400">• {post.createdAt}</span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {post.category}
                  </span>
                  {post.paperId && (
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700">
                      PRAXIS {post.paperId}
                    </span>
                  )}
                </div>
              </div>

              <h3 className="text-base font-bold text-slate-900">{post.title}</h3>
              <p className="text-xs text-slate-600 leading-relaxed">{post.content}</p>

              {/* Tags & Attachment */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1">
                <div className="flex flex-wrap gap-1.5">
                  {post.tags.map((tag, tIdx) => (
                    <span key={tIdx} className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>

                {post.attachmentUrl && (
                  <a
                    href={post.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold border border-indigo-200 transition shrink-0"
                  >
                    <Paperclip className="w-3.5 h-3.5 text-indigo-600" />
                    <span className="truncate max-w-[180px]">{post.attachmentName || 'Download Attachment'}</span>
                  </a>
                )}
              </div>

              {/* Footer Actions */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleUpvote(post.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition font-bold ${
                      post.userUpvoted
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    <span>{post.upvotes} Helpful</span>
                  </button>

                  <button
                    onClick={() => setActivePostId(isExpanded ? null : post.id)}
                    className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 font-semibold"
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                    <span>{(post.replies || []).length} Replies</span>
                  </button>
                </div>

                {post.category === 'Resource Share' && (
                  <button className="text-indigo-600 hover:underline font-bold flex items-center gap-1 text-[11px]">
                    <FileDown className="w-3.5 h-3.5" /> Download Shared Resource
                  </button>
                )}
              </div>

              {/* Replies Section */}
              {isExpanded && (
                <div className="pt-4 border-t border-slate-100 space-y-4 bg-slate-50/50 p-4 rounded-xl">
                  <h4 className="font-bold text-xs text-slate-700">Discussion Replies</h4>

                  <div className="space-y-3">
                    {(post.replies || []).map(reply => (
                      <div key={reply.id} className="p-3 bg-white rounded-xl border border-slate-200 text-xs space-y-1">
                        <div className="flex items-center justify-between font-bold text-slate-900">
                          <span>{reply.userName}</span>
                          <span className="text-slate-400 font-normal text-[10px]">{reply.createdAt}</span>
                        </div>
                        <p className="text-slate-600">{reply.content}</p>
                      </div>
                    ))}
                  </div>

                  {/* Add Reply Form */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Write a helpful response..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      className="flex-1 bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={() => handleAddReply(post.id)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition flex items-center gap-1"
                    >
                      <Send className="w-3.5 h-3.5" /> Reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New Post Modal */}
      {showNewPostModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Start Community Discussion Thread</h3>
              <button onClick={() => setShowNewPostModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Title</label>
                <input
                  type="text"
                  placeholder="e.g. Tips for Core Math word problems?"
                  value={newPostTitle}
                  onChange={(e) => setNewPostTitle(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900 focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Category</label>
                  <select
                    value={newPostCategory}
                    onChange={(e) => setNewPostCategory(e.target.value as any)}
                    className="w-full bg-white border border-slate-300 rounded-xl p-2 text-slate-900"
                  >
                    <option value="Question Help">Question Help</option>
                    <option value="Study Strategy">Study Strategy</option>
                    <option value="Resource Share">Resource Share</option>
                    <option value="State Passing Scores">State Passing Scores</option>
                  </select>
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">Target Paper</label>
                  <select
                    value={newPostPaperId}
                    onChange={(e) => setNewPostPaperId(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-xl p-2 text-slate-900"
                  >
                    {PRAXIS_PAPERS.map(p => (
                      <option key={p.id} value={p.code}>
                        PRAXIS {p.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Discussion Details</label>
                <textarea
                  rows={4}
                  placeholder="Describe your question or share your study resources..."
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900 focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Attach Study Resource / File (Firebase Storage)</label>
                <div className="flex items-center gap-2 border border-slate-300 bg-slate-50 rounded-xl p-2">
                  <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
                  <input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="text-xs text-slate-600 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                  />
                </div>
                {selectedFile && (
                  <p className="text-[11px] text-emerald-600 font-medium mt-1">
                    Selected: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowNewPostModal(false)}
                disabled={uploadingFile}
                className="px-4 py-2 rounded-xl border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePost}
                disabled={uploadingFile}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center gap-2 disabled:opacity-50"
              >
                {uploadingFile ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading to Storage...
                  </>
                ) : (
                  'Post Discussion'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
